// Ad tracking — Meta Pixel + Google Analytics 4 + Google Ads.
//
// Spec: CLAUDE_CODE_TRACKING_PIXEL.md
//
// NOT to be confused with `tracking.js`, which maps internal order statuses
// to what the customer sees. This file only talks to ad platforms.
//
// Three rules this file exists to enforce:
//
//  1. NO ID IS EVER HARD-CODED. Every id comes from an env var. The SAME ids
//     are used on ghostlaundry.com and app.ghostlaundry.com — that shared id
//     is what links "saw the ad" to "placed the order" across the two
//     domains. Different ids would cut the thread and no platform would know
//     the ad produced anything.
//
//  2. EACH TOOL IS INDEPENDENT. A missing var disables that tool only, with
//     a console warning. A visitor never sees an error, and the other tools
//     keep working.
//
//  3. ONE gtag.js. GA4 and Google Ads are the SAME script with two config
//     calls. Loading it twice double-counts everything. Both configs must be
//     present: that is what makes a single gtag('event','purchase') reach
//     GA4 and Google Ads at once.
//
//  4. NO CONVERSION LABEL. Google Ads now matches the conversion on the
//     EVENT NAME against the AW tag. The old gtag('event','conversion',
//     { send_to: 'AW-xxx/label' }) form would not be counted at all.
//
// Nothing here blocks rendering: the scripts are injected after first paint.

const META_ID = import.meta.env.VITE_META_PIXEL_ID || ''
const GA4_ID = import.meta.env.VITE_GA4_ID || ''
const ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID || ''

const CURRENCY = 'USD'

let started = false

// ── low-level senders (always safe to call) ──────────────────────────

function gtag(...args) {
  if (typeof window === 'undefined') return
  if (typeof window.gtag === 'function') window.gtag(...args)
}

function fbq(...args) {
  if (typeof window === 'undefined') return
  if (typeof window.fbq === 'function') window.fbq(...args)
}

const dollars = (cents) => Math.round(Number(cents) || 0) / 100

// ── bootstrap ────────────────────────────────────────────────────────

function loadGoogle() {
  if (!GA4_ID && !ADS_ID) {
    console.warn('[ads] Google skipped — VITE_GA4_ID and VITE_GOOGLE_ADS_ID are both unset')
    return
  }
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line func-names
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments) }
  window.gtag('js', new Date())

  // ONE script tag. Either id can bootstrap it; both are then configured.
  const bootId = GA4_ID || ADS_ID
  if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
    const s = document.createElement('script')
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(bootId)}`
    document.head.appendChild(s)
  }

  if (GA4_ID) window.gtag('config', GA4_ID)
  if (ADS_ID) window.gtag('config', ADS_ID)
}

function loadMeta() {
  if (!META_ID) {
    console.warn('[ads] Meta Pixel skipped — VITE_META_PIXEL_ID is unset')
    return
  }
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'
    n.queue = []; t = b.createElement(e); t.async = !0
    t.src = v; s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  /* eslint-enable */
  window.fbq('init', META_ID)
  window.fbq('track', 'PageView')
}

// Call once, after the first render. Safe to call again — it no-ops.
export function initAdTracking() {
  if (started || typeof window === 'undefined') return
  started = true
  loadGoogle()
  loadMeta()
}

// ── page views ───────────────────────────────────────────────────────

// The initial view is fired by the bootstrap above. This is for SPA route
// changes only — the caller is responsible for skipping the first one.
export function trackPageView(path) {
  gtag('event', 'page_view', { page_path: path })
  fbq('track', 'PageView')
}

// ── the four PWA events ──────────────────────────────────────────────

export function trackSignUp() {
  fbq('track', 'CompleteRegistration')
  gtag('event', 'sign_up', { method: 'email' })
}

export function trackBeginCheckout() {
  fbq('track', 'InitiateCheckout')
  gtag('event', 'begin_checkout', { currency: CURRENCY })
}

// ONE call per pickup, never one per order. `valueCents` is the estimate the
// customer was just shown on screen — this file never computes a price.
export function trackPurchase({ valueCents, orderNumber, services = [] } = {}) {
  const value = dollars(valueCents)
  fbq('track', 'Purchase', {
    value,
    currency: CURRENCY,
    content_type: 'product',
    content_name: services.join(' + ') || 'Pickup',
  })
  // ONE call reaches BOTH destinations, because gtag() is configured for
  // G-xxx and AW-xxx. The Google Ads conversion is matched on the EVENT NAME
  // ('purchase') against the AW tag — there is no conversion label any more,
  // and a gtag('event','conversion',{send_to}) would simply not be counted.
  gtag('event', 'purchase', {
    transaction_id: orderNumber || undefined,
    value,
    currency: CURRENCY,
    items: services.map((name) => ({ item_name: name })),
  })
}

// `subscriptionRef` is the Stripe Checkout Session id from the success URL.
// It must be STABLE: reloading the thank-you page has to replay the same id,
// otherwise Google books a second conversion for one subscription.
export function trackSubscribe({ valueCents = 9500, subscriptionRef } = {}) {
  const value = dollars(valueCents)
  fbq('track', 'Subscribe', { value, currency: CURRENCY })
  gtag('event', 'purchase', {
    transaction_id: subscriptionRef || undefined,
    value,
    currency: CURRENCY,
    items: [{ item_name: 'Ghost Members' }],
  })
  if (!subscriptionRef) {
    console.warn('[ads] subscription purchase sent without transaction_id — a reload could double-count it')
  }
}
